<script lang="ts">
	import Icon from '$lib/components/Icon/Icon.svelte';
	import { _ } from 'svelte-i18n';
	import { isRTL } from '$lib/stores/directionStore';
	import { isLargerThanSmallScreen } from '$lib/stores/resizeStore';
	import type { Instance } from 'flatpickr/dist/types/instance';
	import { onDestroy, onMount } from 'svelte';
	import createCalendar from '$lib/components/DatePicker/calendar';
	import { getHubStores } from '$lib/stores/hubStores';
	import CalendarRange from 'svelte-material-icons/CalendarRange.svelte';

	const { hub } = getHubStores();

	export let startDate: Date | undefined = undefined;
	export let endDate: Date | undefined = undefined;

	let target: HTMLElement;
	let calendar: Instance;

	const getLastMonth = () => {
		const date = new Date();
		date.setMonth(date.getMonth() - 1);
		return date;
	};

	let predefinedRanges: HTMLDivElement;

	let rangeHidden = true;
	onMount(() => {
		if (target) {
			calendar = createCalendar({
				lang: $hub.locale,
				target,
				defaultDate: getLastMonth(),
				range: true,
				onChange: () => {
					startDate = calendar.selectedDates[0];
					endDate = calendar.selectedDates.length == 2 ? calendar.selectedDates[1] : undefined;
				},
				onReady(_, __, fp) {
					if ($isLargerThanSmallScreen) {
						fp.calendarContainer.classList.add('rangePicker');
						fp.calendarContainer.append(predefinedRanges);
						rangeHidden = false;
					}
				},
			});
		}
	});

	onDestroy(() => {
		calendar.destroy();
	});

	const resetDates = () => {
		startDate = undefined;
		endDate = undefined;
	};
	const selectRangeAndClose = (days: number) => {
		endDate = new Date();
		startDate = new Date();
		startDate.setDate(endDate.getDate() - days);
		calendar.close();
	};
	const selectYesterdayAndClose = () => {
		endDate = undefined;
		startDate = new Date();
		startDate.setDate(startDate.getDate() - 1);
		calendar.close();
	};
</script>

<div class="dates" bind:this={target}>
	{#if !startDate && !endDate}
		<CalendarRange color="grey" size="15" />
		{$_('common.date.select.range')}
	{:else}
		<div class="startDate">
			{#if startDate}
				{startDate.toLocaleDateString()}
			{:else}
				{$_('common.dates.start.placeholder')}
			{/if}
		</div>
		<div style={$isRTL ? 'transform: scale(-1)' : ''}>
			<Icon icon="to-arrow" color="grey" size="15" clickable />
		</div>
		<div class="endDate">
			{#if endDate}
				{endDate.toLocaleDateString()}
			{:else}
				{$_('common.dates.end.placeholder')}
			{/if}
		</div>
		<button type="button" class="icon" on:click={resetDates}>
			<Icon icon="close" size="15" color="grey" clickable />
		</button>
	{/if}
</div>

<div bind:this={predefinedRanges} class="predefinedRanges" class:rangeHidden>
	<button type="button" on:click={() => selectRangeAndClose(0)}>{$_('common.dates.today')}</button>
	<button type="button" on:click={selectYesterdayAndClose}>{$_('common.dates.yesterday')}</button>
	<button type="button" on:click={() => selectRangeAndClose(7)}
		>{$_('common.dates.last.7.days')}</button
	>
	<button type="button" on:click={() => selectRangeAndClose(30)}
		>{$_('common.dates.last.30.days')}</button
	>
</div>

<style>
	button {
		all: unset;
		cursor: pointer;
	}

	.dates {
		display: flex;
		align-items: center;
		block-size: 42px;
		padding: var(--gutterSmaller);
		border: 1px solid var(--grayLight);
		border-radius: 0.5rem;
		cursor: pointer;
		gap: 10px;
	}

	.dates:hover {
		color: #5a6171;
	}

	.rangeHidden {
		display: none;
	}

	.predefinedRanges {
		grid-column: 3;
		grid-row: 1 / span 2;
		padding-block-start: 60px;
		padding-inline: var(--gutterSmall);
		background: #eceef1;
		box-shadow: 0 3px 13px rgb(0 0 0 / 8%);
	}

	.predefinedRanges > button {
		inline-size: max-content;
		color: #5a6171;
		font-size: 15px;
		font-weight: 700;
		cursor: pointer;
	}

	:global(.rangePicker .flatpickr-months) {
		position: relative;
		grid-column: 2;
		grid-row: 1;
	}

	:global(.rangePicker .flatpickr-innerContainer) {
		grid-column: 2;
		grid-row: 2;
		overflow: visible;
	}

	:global(.rangePicker.open) {
		display: grid;
		inline-size: auto;
	}
</style>
